import { useCallback, useEffect, useRef, useState } from 'react';
import bannerVideoService, {
  BannerVideoEntry,
  CreateBannerVideoRequest,
} from '../services/api/bannerVideoService';

export const isBannerVideoActive = (entry: BannerVideoEntry) =>
  entry.job.status === 'pending' || entry.job.status === 'processing';

const errorMessage = (error: unknown): string => {
  const response = (error as { response?: { data?: { message?: string } } })
    ?.response;
  return (
    response?.data?.message ||
    (error instanceof Error
      ? error.message
      : '動画の生成を開始できませんでした。もう一度お試しください。')
  );
};

/** A server-side request ID survives retries whose response was lost. */
const requestId = (): string => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export default function useBannerVideo(imageId: number) {
  const [entries, setEntries] = useState<BannerVideoEntry[]>([]);
  const [selectedId, setSelectedId] = useState('image');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const mounted = useRef(true);
  const submitting = useRef(false);
  const pendingRequest = useRef<CreateBannerVideoRequest | null>(null);
  const refreshMediaUrls = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const jobs = await bannerVideoService.getForImage(imageId);
        if (cancelled) return;
        const replaceUrls = refreshMediaUrls.current;
        refreshMediaUrls.current = false;
        setEntries((current) =>
          jobs.map((entry) => {
            const previous = current.find(
              (item) => item.job.id === entry.job.id
            );
            // Signed URLs change on each poll; keep the playing source stable.
            if (
              !replaceUrls &&
              entry.video &&
              previous?.video?.id === entry.video.id
            ) {
              return {
                ...entry,
                video: { ...entry.video, url: previous.video.url },
              };
            }
            return entry;
          })
        );
        setSyncError(null);
        if (jobs.some(isBannerVideoActive)) timer = setTimeout(load, 3000);
      } catch {
        if (cancelled) return;
        setSyncError(
          '動画の状態を確認できません。通信が戻ると自動で再確認します。'
        );
        timer = setTimeout(load, 5000);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [imageId, refreshKey]);

  const create = useCallback(
    async (sourceVideoId?: number, instruction?: string) => {
      if (submitting.current || entries.some(isBannerVideoActive)) return;
      submitting.current = true;
      setIsSubmitting(true);
      setSelectedId('starting');
      setError(null);

      const previous = pendingRequest.current;
      const request =
        previous &&
        previous.source_video_id === sourceVideoId &&
        previous.instruction === instruction
          ? previous
          : {
              source_image_id: imageId,
              source_video_id: sourceVideoId,
              instruction,
              client_request_id: requestId(),
            };
      pendingRequest.current = request;
      try {
        const entry = await bannerVideoService.create(request);
        pendingRequest.current = null;
        if (!mounted.current) return;
        setEntries((current) => [
          ...current.filter((item) => item.job.id !== entry.job.id),
          entry,
        ]);
        setSelectedId(entry.job.id);
        setRefreshKey((key) => key + 1);
      } catch (err) {
        if (!mounted.current) return;
        setError(errorMessage(err));
        if (
          (err as { response?: { status?: number } })?.response?.status === 409
        ) {
          setRefreshKey((key) => key + 1);
        }
      } finally {
        submitting.current = false;
        if (mounted.current) setIsSubmitting(false);
      }
    },
    [entries, imageId]
  );

  const activeEntry = entries.find(isBannerVideoActive);
  const openVideo = () => {
    const entry = activeEntry || entries[entries.length - 1];
    if (entry) setSelectedId(entry.job.id);
    else if (isSubmitting || pendingRequest.current) setSelectedId('starting');
    else void create();
  };

  return {
    entries,
    selectedId,
    setSelectedId,
    isLoading,
    isSubmitting,
    error,
    syncError,
    activeEntry,
    create,
    openVideo,
    retrySubmission: () => {
      const request = pendingRequest.current;
      void create(request?.source_video_id, request?.instruction);
    },
    refresh: () => {
      refreshMediaUrls.current = true;
      setRefreshKey((key) => key + 1);
    },
  };
}
